import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import { formatPrice } from '../../../utils/helpers';
import { getPropertyUrl } from '../../../utils/urlHelpers';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';
import { firstImageOrPlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../../utils/imageFallback';
import propertyService from '../../../services/propertyService';

const SocietyProperty = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    property_type: 'all',
    price_range: 'all',
    sort: 'newest'
  });

  const sortToOrdering = {
    newest: '-created_at',
    oldest: 'created_at',
    price_low: 'price',
    price_high: '-price',
    area_large: '-size',
    area_small: 'size',
  };

  // Load properties
  useEffect(() => {
    const loadProperties = async () => {
      if (!society?.id) {
        return;
      }

      setLoading(true);
      try {
        const response = await propertyService.getProperties({
          society: society.id,
          page: currentPage,
          page_size: 12,
          is_public: true,
          search: filters.search || undefined,
          property_type: filters.property_type !== 'all' ? filters.property_type : undefined,
          ordering: sortToOrdering[filters.sort] || '-created_at',
        });

        const propertiesData = response.results || [];
        setProperties(propertiesData);
        setTotalPages(response.total_pages || Math.ceil((response.count || 0) / 12) || 1);
        setTotalCount(response.count || propertiesData.length);
      } catch (error) {
        console.error('Error loading properties:', error);
        if (onError) {
          onError('Failed to load properties');
        }
      } finally {
        setLoading(false);
      }
    };

    loadProperties();
  }, [society?.id, currentPage, filters, onError]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const getTypeColor = (type) => {
    const colors = {
      house: 'bg-blue-100 text-blue-800',
      apartment: 'bg-green-100 text-green-800',
      commercial: 'bg-purple-100 text-purple-800',
      plot: 'bg-orange-100 text-orange-800',
      villa: 'bg-red-100 text-red-800'
    };
    return colors[type] || colors.house;
  };

  // Safety check
  if (!society) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center text-gray-500">
          <p>Society information not available</p>
        </div>
      </div>
    );
  }

  const activeFilterCount =
    (filters.property_type !== 'all' ? 1 : 0) +
    (filters.price_range !== 'all' ? 1 : 0) +
    (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Property"
        count={totalCount}
        itemLabel={{ singular: 'property', plural: 'properties' }}
        societyName={society.name}
        searchPlaceholder="Search properties..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'List Property',
                onClick: () =>
                  navigate(`/societies/${society.slug}/properties/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Property Type"
          value={filters.property_type}
          onChange={(e) => handleFilterChange({ ...filters, property_type: e.target.value })}
        >
          <option value="all">All Types</option>
          <option value="for_sale">For Sale</option>
          <option value="for_rent">For Rent</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Price Range"
          value={filters.price_range}
          onChange={(e) => handleFilterChange({ ...filters, price_range: e.target.value })}
        >
          <option value="all">All Prices</option>
          <option value="under_1m">Under 1M</option>
          <option value="1m_5m">1M - 5M</option>
          <option value="5m_10m">5M - 10M</option>
          <option value="10m_20m">10M - 20M</option>
          <option value="over_20m">Over 20M</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="price_low">Price: Low to High</option>
          <option value="price_high">Price: High to Low</option>
          <option value="area_large">Area: Large to Small</option>
          <option value="area_small">Area: Small to Large</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Properties Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : properties.length > 0 ? (
          properties.map((property) => (
            <div 
              key={property.id} 
              className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(getPropertyUrl(property, society.slug))}
            >
              {/* Property Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center relative group overflow-hidden">
                <>
                  <img
                    src={firstImageOrPlaceholder(property.images)}
                    alt={property.title || 'Property image'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    style={{ objectFit: 'cover' }}
                    onError={(e) => {
                      e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                    }}
                  />
                  {/* Image Count Badge */}
                  {property.images && property.images.length > 1 && (
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs">
                      +{property.images.length - 1}
                    </div>
                  )}
                  {/* View Gallery Indicator */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </>
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    property.property_type === 'for_rent' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {property.property_type === 'for_rent' ? 'For Rent' : 'For Sale'}
                  </span>
                </div>
                {!property.is_public && (
                  <div className="absolute top-2 left-2 mt-6">
                    <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Members Only
                    </span>
                  </div>
                )}
              </div>

              {/* Property Info */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                  {property.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {property.description}
                </p>

                <div className="text-2xl font-bold text-blue-600 mb-3">
                  {formatPrice(property.price)}
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                  {property.size && property.size_unit && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      <span>{property.size} {property.size_unit}</span>
                    </div>
                  )}
                  {property.bedrooms && property.bedrooms > 0 && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      </svg>
                      <span>{property.bedrooms} bed{property.bedrooms !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {property.bathrooms && property.bathrooms > 0 && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                      <span>{property.bathrooms} bath{property.bathrooms !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {property.address && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="line-clamp-1">{property.address}</span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No properties yet</h3>
            <p className="text-gray-500 mb-4">Be the first to list a property in this society.</p>
            {isAuthenticated && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/societies/${society.slug}/properties/create?society=${society.id}`)
                }
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
              >
                List First Property
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyProperty;
